import { HttpClient, HttpParams } from "@angular/common/http";
import { Injectable } from "@angular/core";
import { filter, take, switchMap, of } from "rxjs";
import { Action, State, StateContext } from "@ngxs/store";
import { RequestState, createRequestAction } from "ngxs-requests-plugin";

import { environment } from "../../../../environments/environment";
import { AppConfigService } from "../../services/app-config.service";
import { treeFlattenerFunction } from "../../../shared/utils/tree-flattener-function";
import { patchIdsAndEntities } from "../../../shared/utils/patch-ids-and-entities";
import { IBasicVariableInfo, ISearchContentItem, ISearchSubTreeContentItem, IVariable } from "../../interfaces/variable.interface";

import { ClearSearchedSubTreeVariable, ClearSearchedVariable, ClearVariableInfo, ClearVariableValueSearchResults, GetVariableInfo, GetVariableInfoFail, GetVariableInfoSuccess, GetVariablesPreset, GetVariablesPresetFail, GetVariablesPresetList, GetVariablesPresetListFail, GetVariablesPresetListSuccess, GetVariablesPresetSuccess, GetVariablesTree as GetVariableTree, GetVariablesTreeFail, GetVariablesTreeSuccess, SearchSubTreeVariable, SearchSubTreeVariableFail, SearchSubTreeVariableSuccess, SearchVariable, SearchVariableFail, SearchVariableSuccess, SearchVariableValue, SearchVariableValueFail, SearchVariableValueSuccess } from "./variables.actions";

import { statusCodeErrorHandler } from "../../../shared/utils/status-code-error-handler";
import { IPresetReduced } from "../../interfaces/presets.interface";
import { LocalStorageService } from "../../services/local-storage.service";
import { AuthService } from "../../services/auth.service";
import { flattenTreeToKeyValue } from "../../../shared/utils/flatten-tree-to-keyvalue";

export interface VariablesStateModel {
  ids: string[],
  entities: {[key: string]: IVariable},
  variableValuesSearchResults: {label: string, value: string}[],
  flatVariablesList: {[key: string]: IVariable},
  variableInstruction: string,
  searchResults: ISearchContentItem[],
  searchSubTreeResults: ISearchSubTreeContentItem[],
  variablesPresetList: IPresetReduced[],
  variablesPresetName: string,
  variablesPreset: string[],
  tagInfo: { [key: string]: IBasicVariableInfo },
}

@RequestState('getVariablesTree')
@Injectable()
export class GetVariablesTreeRequestState {}

@RequestState('getVariableInfo')
@Injectable()
export class GetVariableInfoRequestState {}

@RequestState('getVariablesPresetList')
@Injectable()
export class GetVariablesPresetListRequestState {}

@RequestState('getVariablesPreset')
@Injectable()
export class GetVariablesPresetRequestState {}

@RequestState('searchVariable')
@Injectable()
export class SearchVariableRequestState {
}

@RequestState('searchSubTreeVariable')
@Injectable()
export class SearchSubTreeVariableRequestState {
}

@RequestState('searchVariableValue')
@Injectable()
export class SearchVariableValueRequestState {
}

@State<VariablesStateModel>({
  name: 'variables',
  defaults: {
    ids: [],
    entities: null,
    variableValuesSearchResults: null,
    flatVariablesList: null,
    variableInstruction: null,
    searchResults: null,
    searchSubTreeResults: null,
    variablesPresetList: null,
    variablesPresetName: null,
    variablesPreset: null,
    tagInfo: {},
  }
})
@Injectable()
export class VariablesState {
  constructor(
    private http: HttpClient,
    private localStorageService: LocalStorageService,
    private authService: AuthService,
    private appConfig: AppConfigService,
  ) {}

  @Action(GetVariableTree)
  getVariableTree(
    { getState, dispatch }: StateContext<VariablesStateModel>
  ) {
    const state = getState();

    // Skip if tree is already loaded
    if (state.ids && state.ids.length > 0) {
      return of(void 0);
    }

    return this.authService.currentUser$.pipe(
      filter(user => !!user),
      take(1),
      switchMap(user => {
        const lang = user?.lang || 'en';
        const dbVersion = this.localStorageService.dbVersionSubject$.getValue() || 'latest';
        const params = new HttpParams({ fromObject: {dbName: this.appConfig.dbName, dbVersion: dbVersion, lang: lang} });
        const request = this.http.get(environment.api + 'db/meta/variables/tree', {params});
      
        return dispatch(
          createRequestAction({
            request,
            state: GetVariablesTreeRequestState,
            successAction: GetVariablesTreeSuccess,
            failAction: GetVariablesTreeFail,
          })
        );
      })
    );
  }

  @Action(GetVariablesTreeSuccess)
  getVariablesTreeSuccess(
    ctx: StateContext<VariablesStateModel>,
    { payload: response }: GetVariablesTreeSuccess
  ) {
    patchIdsAndEntities(ctx, [...response.content.children], true, 'tag');

    ctx.patchState({flatVariablesList: treeFlattenerFunction([...response.content.children])});
    ctx.patchState({tagInfo: flattenTreeToKeyValue(response.content, 'tag', 'name', 'children')});
  }

  @Action(GetVariablesTreeFail)
  getVariablesTreeFail(
    { dispatch }: StateContext<VariablesStateModel>,
    { payload: error }: GetVariablesTreeFail
  ) {
    statusCodeErrorHandler(error, dispatch)
  }

  @Action(GetVariableInfo)
  getVariableInfo(
    { dispatch }: StateContext<VariablesStateModel>,
    { payload: tag }: GetVariableInfo
  ) {
    return this.authService.currentUser$.pipe(
      filter(user => !!user),
      take(1),
      switchMap(user => {
        const lang = user?.lang || 'en';
        const params = new HttpParams({ fromObject: {tag: tag, dbName: this.appConfig.dbName, dbVersion: "latest", lang: lang} });
        const request = this.http.get(environment.api + 'db/meta/variables/info', {params});

        return dispatch(
          createRequestAction({
            request,
            state: GetVariableInfoRequestState,
            successAction: GetVariableInfoSuccess,
            failAction: GetVariableInfoFail
          })
        );
      })
    );
  }

  @Action(GetVariableInfoSuccess)
  getVariableInfoSuccess(
    { patchState }: StateContext<VariablesStateModel>,
    { payload: response }: GetVariableInfoSuccess
  ) {
    patchState({variableInstruction: response.content});
  }

  @Action(ClearVariableInfo)
  clearVariableInfo(
    { patchState }: StateContext<VariablesStateModel>
  ) {
    patchState({variableInstruction: null});
  }

  @Action(SearchVariable)
  searchVariable(
    { dispatch }: StateContext<VariablesStateModel>,
    { payload: input }: SearchVariable
  ) {
    const params = new HttpParams().set('q', input);
    const request = this.http.get(environment.api + 'search/variable', {params});

    return dispatch(
      createRequestAction({
        request,
        state: SearchVariableRequestState,
        successAction: SearchVariableSuccess,
        failAction: SearchVariableFail,
      })
    );
  }

  @Action(ClearSearchedVariable)
  clearSearchedVariable(
    { patchState }: StateContext<VariablesStateModel>
  ) {
    patchState({searchResults: null});
  }

  @Action(SearchSubTreeVariable)
  searchSubTreeVariable(
    { dispatch }: StateContext<VariablesStateModel>,
    { payload: input }: SearchSubTreeVariable
  ) {
    const params = new HttpParams().set('q', input.query);
    const request = this.http.get(`${environment.api}search/tree/${encodeURIComponent(input.filterTreeName)}`, {params});

    return dispatch(
      createRequestAction({
        request,
        state: SearchSubTreeVariableRequestState,
        successAction: SearchSubTreeVariableSuccess,
        failAction: SearchSubTreeVariableFail,
      })
    );
  }

  @Action(SearchSubTreeVariableSuccess)
  searchSubTreeVariableSuccess(
    { patchState }: StateContext<VariablesStateModel>,
    { payload: response }: SearchSubTreeVariableSuccess
  ) {
    patchState({searchSubTreeResults: response.content});
  }

  @Action(ClearSearchedSubTreeVariable)
  clearSearchedSubTreeVariable(
    { patchState }: StateContext<VariablesStateModel>
  ) {
    patchState({searchSubTreeResults: null});
  }

  @Action(GetVariablesPresetList)
  getVariablesPresetList(
    { dispatch }: StateContext<VariablesStateModel>,
  ) {
    const params = new HttpParams().set('dbName', this.appConfig.dbName);
    const request = this.http.get(environment.api + 'user/presets/variables/list', {params});

    return dispatch(
      createRequestAction({
        request,
        state: GetVariablesPresetListRequestState,
        successAction: GetVariablesPresetListSuccess,
        failAction: GetVariablesPresetListFail
      })
    );
  }

  @Action(GetVariablesPresetListSuccess)
  getVariablesPresetListSuccess(
    { patchState }: StateContext<VariablesStateModel>,
    { payload: response }: GetVariablesPresetListSuccess
  ) {
    const variablesLists = response.content.map(item => ({
      ...item,
      last_accessed_at: new Date(item.last_accessed_at)
    }));
    patchState({variablesPresetList: variablesLists});
  }

  @Action(GetVariablesPresetListFail)
  getVariablesPresetListFail(
    { patchState }: StateContext<VariablesStateModel>,
    { payload: response }: GetVariablesPresetListSuccess
  ) {
    patchState({variablesPresetList: []});
  }

  @Action(GetVariablesPreset)
  getVariablesPreset(
    { dispatch, patchState }: StateContext<VariablesStateModel>,
    { payload: name }: GetVariablesPreset
  ) {
    const params = new HttpParams().set('dbName', this.appConfig.dbName).set('name', name);
    const request = this.http.get(environment.api + 'user/presets/variables/get', {params});
    patchState({variablesPresetName: name, variablesPreset: null});

    return dispatch(
      createRequestAction({
        request,
        state: GetVariablesPresetRequestState,
        successAction: GetVariablesPresetSuccess,
        failAction: GetVariablesPresetFail
      })
    );
  }

  @Action(GetVariablesPresetSuccess)
  getVariablesPresetSuccess(
    { patchState, getState }: StateContext<VariablesStateModel>,
    { payload: response }: GetVariablesPresetSuccess
  ) {
    patchState({variablesPreset: response.content});
  }

  @Action(SearchVariableSuccess)
  searchVariableSuccess(
    { patchState }: StateContext<VariablesStateModel>,
    { payload: response }: SearchVariableSuccess
  ) {
    patchState({searchResults: response.content});
  }

  @Action(SearchVariableValue)
  searchVariableValue(
    { dispatch }: StateContext<VariablesStateModel>,
    { payload: {variableName, input} }: SearchVariableValue
  ) {
    const params = new HttpParams().set('q', input);
    const request = this.http.get(environment.api + 'search/value/' + encodeURIComponent(variableName), {params});

    return dispatch(
      createRequestAction({
        request,
        state: SearchVariableValueRequestState,
        successAction: SearchVariableValueSuccess,
        failAction: SearchVariableValueFail,
      })
    );
  }

  @Action(SearchVariableValueSuccess)
  searchVariableValueSuccess(
    { patchState }: StateContext<VariablesStateModel>,
    { payload: response }: SearchVariableValueSuccess
  ) {
    patchState({variableValuesSearchResults: response.content});
  }

  @Action(ClearVariableValueSearchResults)
  clearVariableValueSearchResults(
    { patchState }: StateContext<VariablesStateModel>,
  ) {
    patchState({variableValuesSearchResults: null});
  }
}